/**
 * Creators Partnership Agreement — verbatim transcription from
 * `CONTENT CREATION AGREEMENT_Template.pdf` (Taboo Studios LLC).
 *
 * Edit only when the legal text changes. Keep section numbering intact.
 */
export function ContractBody() {
  return (
    <article className="space-y-5 text-sm leading-relaxed text-text-secondary">
      <header className="space-y-3">
        <h2 className="text-lg font-bold text-text-primary">Creators Partnership Agreement</h2>
        <p>
          THIS AGREEMENT (this &ldquo;Agreement&rdquo;) is entered into between{' '}
          <strong className="text-text-primary">TABOO STUDIOS LLC</strong>, a company duly
          incorporated and validly existing under the laws of the United States, with its principal
          place of business at 30 N GOULD ST SHERIDAN, WY 82801, USA (the &ldquo;Company&rdquo; or
          &ldquo;CONTRACTING PARTY&rdquo;); and any individual or entity that registers as a creator
          on the platform (&ldquo;Creator&rdquo;).
        </p>
        <p>
          The Creator expressly acknowledges that acceptance of this Agreement may occur
          electronically, including by clicking a checkbox, button, or similar mechanism indicating
          acceptance. Such electronic acceptance shall have the same legal effect as a handwritten
          signature.
        </p>
      </header>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">Recitals</h3>
        <p>
          WHEREAS the Company operates a digital media and entertainment platform with a significant
          presence in online content distribution.
        </p>
        <p>
          WHEREAS the Creator is a content creator specializing in digital media production and
          distribution.
        </p>
        <p>
          WHEREAS the Company desires to engage the Creator to provide artistic services, including
          but not limited to hosting, presenting, and creating content, as well as granting rights
          over personality attributes and intellectual property.
        </p>
        <p>
          Now, therefor, in consideration of the mutual covenants set forth herein, the Parties
          agree as follows:
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">1. Services Provided</h3>
        <p>
          1.1. The Creator shall provide the following services (the &ldquo;Services&rdquo;): (i)
          The creation of content, including but not limited to video hosting and digital
          engagement; (ii) The authorization for the Company to use the Creator&rsquo;s name, image,
          likeness, and voice in audiovisual productions.
        </p>
        <p>
          1.2. The Creator shall create and publish between 12 and 15 videos per year on the
          Company&rsquo;s platform. The minimum acceptable number of videos per year shall be (12)
          twelve.
        </p>
        <p>
          1.3. The Creator shall make commercially reasonable efforts to fulfill Creator&rsquo;s
          obligations under this Agreement, ensuring the timely and professional execution of all
          assigned tasks. The Creator shall grant the Creator reasonable access to relevant
          information and resources necessary for the proper performance of Creator&rsquo;s
          obligations hereunder.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">2. Compensation</h3>
        <p>
          2.1. The Creator shall be entitled to a revenue share based on the number of users they
          bring to Taboo.TV, as tracked by the platform. To facilitate this, the Company will
          provide the Creator with a unique referral link and/or a personalized promotional code,
          which must be used for audience promotion.
        </p>
        <p>
          2.2. The Creator will receive 85% of the total revenue generated during the first month
          from new subscribers who sign up for a monthly subscription using the Creator&rsquo;s
          referral link or promotional code. After the first month, the Creator will receive 85% of
          the recurring monthly revenue from these subscribers for as long as their subscriptions
          remain active. For annual subscription purchases made through the Creator&rsquo;s referral
          link or promotional code, the Creator will receive 55% of the total revenue generated from
          those subscriptions.
        </p>
        <p>
          2.3. Additional performance-based incentives may be granted, including but not limited to
          milestone bonuses (based on the number of acquired subscribers), retention bonuses (for
          maintaining a specific percentage of subscribers over time), and referral bonuses (for
          bringing in new creators), as outlined in Taboo TV&rsquo;s bonus plans.
        </p>
        <p>
          <strong className="text-text-primary">2.4. Equity Compensation:</strong> The Creator may,
          at the sole discretion of the Company, be eligible for additional compensation in the form
          of equity, provided that:
        </p>
        <ol className="ml-5 list-[lower-alpha] space-y-1">
          <li>
            The Creator meets specific performance, engagement, and content production criteria as
            determined by the Company.
          </li>
          <li>
            The Creator agrees to distribute their content primarily through the Company&rsquo;s
            platform for a period specified by mutual agreement.
          </li>
          <li>
            The Company, at its sole discretion, shall determine the applicable equity percentage,
            vesting schedule, and any additional conditions for such equity compensation.
          </li>
          <li>
            If the Creator is deemed eligible, the parties shall enter into a separate written
            agreement outlining the specific terms, rights, and obligations associated with the
            equity grant.
          </li>
        </ol>
        <p>
          2.5. The Company shall conduct a monthly reconciliation of performance metrics,
          considering the subscribers acquired through the Creator&rsquo;s referral link and/or
          promotional code, to determine any compensation owed.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">
          3. Intellectual Property Rights
        </h3>
        <p>
          3.1. The Creator acknowledges and agrees that uploading or publishing content on the
          Company&rsquo;s platform does not transfer ownership of such content to the Company. The
          Creator retains full ownership of all intellectual property rights associated with their
          content.
        </p>
        <p>
          3.2. By using the platform, the Creator grants the Company a non-exclusive, worldwide,
          sublicensable, and royalty-free license to use, distribute, modify, display, perform, and
          monetize the content in any format or medium, whether now known or developed in the
          future. This license shall remain in effect indefinitely, unless revoked by the Creator.
        </p>
        <p>
          3.3. The Creator shall maintain full control over the management of their content,
          including the ability to remove it from the platform. However, if the Creator intends to
          remove a substantial portion of their content or engage in bulk content removal, they must
          provide the Company with at least thirty (30) days&rsquo; prior written notice. The
          removal of individual pieces of content on an occasional basis shall not be subject to
          this notice requirement.
        </p>
        <p>
          3.4. The Company retains full discretion to use the licensed content for commercial
          purposes, including but not limited to advertising, sublicensing, promotional activities,
          and monetization, provided that such use does not infringe on the Creator&rsquo;s moral
          rights or misrepresent the Creator&rsquo;s association with the content.
        </p>
        <p>
          3.5. The Company shall have the right to monetize and sublicense the Creator&rsquo;s
          content without any obligation for additional compensation beyond what is expressly
          stipulated in this Agreement.
        </p>
        <p>
          3.6. The Creator may revoke the license granted under Section 3.1(b) by providing the
          Company with written notice at least thirty (30) days in advance. Upon the expiration of
          such notice period, the Company shall cease new uses of the content but shall not be
          required to remove previously distributed or sublicensed copies of the content that were
          already in circulation prior to the termination date.
        </p>
        <p>
          3.7. The Company reserves the right to continue using any content that was incorporated
          into derivative works, marketing materials, or sublicensed agreements before the
          termination notice became effective.
        </p>
        <p>
          3.8. The Creator agrees to use their best efforts to make Taboo their primary paid content
          platform. While they may maintain other paid accounts (such as Patreon), they should
          actively work to minimize their presence on those platforms and focus on growing their
          Taboo user base. The Creator shall not publish, license, or distribute similar content on
          any competing platform without the Company&rsquo;s prior written consent.
        </p>
        <p>
          3.8. Notwithstanding the exclusivity commitment, the Creator may distribute their content
          in different formats on social media and/or platforms that do not directly compete with or
          operate within the same segment as the Company.
        </p>
        <p>
          3.9. If the Creator receives offers to distribute content on streaming platforms that
          indirectly compete with the Company, such as Disney, Netflix, or similar entities, the
          Creator may request written authorization from the Company to allow flexibility in the
          exclusivity clause. The Company, at its sole discretion, may grant such authorization, and
          such flexibility shall not constitute a breach of this Agreement.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">4. Term and Termination</h3>
        <p>
          4.1. This Agreement shall remain in effect from the Effective Date and shall automatically
          renew indefinitely unless either party provides written notice of termination in
          accordance with Section 4 - Term and Termination.
        </p>
        <p>
          4.2. Either Party may terminate this Agreement with (60) sixty days&rsquo; written notice.
          Termination without cause shall not affect any accrued compensation or obligations
          incurred prior to the termination date.
        </p>
        <p>
          4.3. In the event of a material breach by either Party, the non-breaching Party shall
          provide written notice and a (15) fifteen-day cure period before seeking termination and
          damages.
        </p>
        <p>
          4.4. Upon termination of this Agreement, whether by expiration, breach, or any other
          cause, the Creator shall continue to receive any earned but unpaid compensation,
          commissions, or revenue share accrued prior to termination. However, the Creator
          acknowledges that no new earnings shall be generated from subscription renewals or
          recurring revenues after the termination date.
        </p>
        <p>
          4.5. The Company shall have no obligation to make any additional payments or provide any
          ongoing financial benefits to the Creator in connection with previously acquired users,
          irrespective of the duration of their continued subscriptions or recurring payments.
        </p>
        <p>
          4.6. The Creator expressly waives any claim, past, present, or future, to any
          subscription-based or recurring revenue associated with users acquired through the
          Creator&rsquo;s promotional efforts, following the termination of this Agreement.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">
          5. Representations, Warranties, and Indemnities
        </h3>
        <p>
          <strong className="text-text-primary">5.1. Creator Representation and Warranties:</strong>{' '}
          The execution, delivery, and performance of this Agreement by the Creator are fully within
          the Creator&rsquo;s legal capacity and authority and do not violate any existing
          agreements, obligations, or laws to which the Creator is bound.
        </p>
        <p>
          <strong className="text-text-primary">5.2. Company Representation and Warranties:</strong>{' '}
          The Company represents, warrants, and agrees that the execution, delivery, and performance
          of this Agreement by the Company are fully within its power and do not violate any other
          agreements or obligations.
        </p>
        <p className="ml-5">
          5.2.1. The Company affirms that it holds all necessary rights to distribute and
          commercialize content created under this Agreement without infringing on third-party
          rights.
        </p>
        <p>
          5.3. Commitment to Freedom of Expression and Content Responsibility: (a) The Company is
          committed to being a censorship-free platform that unconditionally supports the freedom of
          expression of its Creators. Taboo.TV welcomes all forms of creative content, including
          opinions, humor, and diverse perspectives, provided that such content does not infringe
          upon the laws of the United States. The Creator shall not be banned, restricted, or
          penalized merely for expressing their views or artistic choices. (b) While the Company
          provides an open and inclusive space for content creation, the Creator acknowledges that
          they remain fully responsible for the content they produce and share. The Company does not
          interfere with creative expression but also does not assume liability for any disputes,
          claims, or damages arising from the Creator&rsquo;s content. (c) To maintain a safe and
          legally compliant environment, the Creator shall make reasonable efforts to avoid
          publishing content that contains illegal pornography, explicit hate speech, or incitement
          to violence.{' '}
          <strong className="text-text-primary">
            However, the Company respects artistic freedom and trusts the Creator&rsquo;s judgment
            in producing content that aligns with their vision, if it remains within legal
            boundaries
          </strong>
          . (d) The Creator agrees to comply with all applicable U.S. laws and regulations and
          acknowledges that they are solely responsible for any legal implications related to their
          content. In the event of any governmental or third-party claims, the Creator shall assume
          full responsibility, indemnifying and holding the Company harmless from any civil or
          criminal liability.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">6. Anti-Corruption Compliance</h3>
        <p>
          6.1. The Creator acknowledges and agrees to comply with all applicable laws and
          regulations concerning the prevention of corruption and money laundering.
        </p>
        <p>
          6.2. The Creator shall not offer, promise, or provide, directly or indirectly, any
          financial or other benefits to any government official or individual in exchange for undue
          influence or to gain an unfair advantage.
        </p>
        <p>
          6.3. The Creator shall ensure that any subcontractors or third parties engaged in
          fulfilling obligations under this Agreement comply with similar anti-corruption standards.
        </p>
        <p>
          6.4. The Company reserves the right to audit the Creator&rsquo;s records and financial
          transactions related to this Agreement to ensure compliance with this Agreement.
        </p>
        <p>
          6.5. A breach of this Section shall be considered a material breach of this Agreement,
          entitling the Company to terminate the Agreement immediately without liability.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">7. Notices</h3>
        <p>
          7.1. Notices may also be provided via electronic means, including email or in-platform
          notifications, which shall be deemed valid and sufficient.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">8. Indemnification</h3>
        <p>
          8.1. The Creator shall indemnify and hold harmless the Company, its officers, employees,
          agents, assigns, and affiliates (the &ldquo;Company Parties&rdquo;) from and against all
          third-party claims, damages, liabilities, costs, and expenses, including reasonable
          attorneys&rsquo; fees, arising from: (i) Any breach of the Creator&rsquo;s
          representations, warranties, or obligations under this Agreement; (ii) Any violation of
          intellectual property rights, privacy rights, or publicity rights by the Creator&rsquo;s
          content or actions; (iii) Any failure to comply with applicable tax obligations related to
          compensation received under this Agreement.
        </p>
        <p>
          8.2. The Company shall indemnify and hold harmless the Creator from any third-party claims
          arising from: (i) The Company&rsquo;s use, distribution, or commercialization of content
          produced under this Agreement, provided that such use is consistent with the terms herein;
          (ii) Any gross negligence or wrongful misconduct of the Company.
        </p>
        <p>
          8.3. If a claim arises that may trigger an indemnification obligation, the indemnified
          party (&ldquo;Indemnitee&rdquo;) shall notify the indemnifying party
          (&ldquo;Indemnitor&rdquo;) in writing as soon as reasonably possible. The Indemnitor shall
          have the right to assume the defense of such a claim with legal counsel of its choosing,
          subject to the Indemnitee&rsquo;s reasonable approval.
        </p>
        <p>
          8.4. The Indemnitee shall cooperate with the Indemnitor in the defense of any claims and
          shall not settle any claims without prior written consent from the Indemnitor, which
          consent shall not be unreasonably withheld.
        </p>
        <p>
          8.5. The obligations under this Section shall survive the termination or expiration of
          this Agreement.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">
          9. Governing Law, Dispute Resolution and General Agreement
        </h3>
        <p>
          9.1. This Agreement shall be governed by and construed in accordance with the laws of the
          State of Wyoming.
        </p>
        <p>
          9.2. Any disputes arising from this Agreement shall be submitted to the exclusive
          jurisdiction of the courts of Wyoming, with the prevailing Party entitled to recover
          reasonable attorneys&rsquo; fees and costs.
        </p>
        <p>
          9.3. Waiver. The failure of a party to exercise any of the rights granted to such party
          upon the occurrence of any of the contingencies set forth in this Agreement or its failure
          to insist upon strict adherence to any term or condition of this Agreement on any one
          occasion shall not be construed a waiver or deprive that party of the right thereafter to
          insist upon strict adherence to that or any of the other terms and conditions of this
          Agreement at any time.
        </p>
        <p>
          9.4. Voluntary &amp; Knowing Agreement. Creator expressly acknowledges and agrees that it
          has read this Agreement, has had an opportunity to ask questions and have them explained
          to Creator, and that Creator has had the opportunity to consult with an attorney or other
          advisor of its choice regarding this Agreement. Creator represents that it has not been
          induced to execute this Agreement by any statement, act or representation of any kind or
          character on the part of anyone, except as contained in the Agreement. Creator has entered
          into this Agreement voluntarily and without coercion or duress.
        </p>
        <p>
          9.5. Entire Understanding. These terms and conditions constitute the entire understanding
          with respect to the subject matter of this Agreement and supersede all prior or
          inconsistent agreements, oral or otherwise.
        </p>
        <p>
          9.6. The Company may amend this Agreement from time to time. Updated versions will be made
          available on the platform and shall become effective upon publication or upon the
          Creator&rsquo;s continued use of the platform. Electronic acceptance and continued use
          shall constitute valid and binding acceptance of any amendments.
        </p>
        <p>
          9.7. Paragraph Headings. Paragraph headings are for reference purposes only and are not
          intended to and shall not create substantive rights or obligations or be used to interpret
          meaning.
        </p>
        <p>
          9.8. Survival of Terms. The warranties, representations, the indemnity rights and other
          remedies afforded to each party to this Agreement and any other terms that must survive to
          give effect to their meaning, shall survive the termination and/or expiration of this
          Agreement.
        </p>
        <p>
          9.9. Counterparts. This Agreement may be executed in any number of counterparts, each of
          which shall be deemed an original and when taken together shall constitute one document.
          The parties agree that facsimile and pdf and other electronic signatures shall have the
          same force and effect as though they were original signatures.
        </p>
        <p>
          9.10. Enforceability of Terms. If any provision of this Agreement is invalid or
          unenforceable, that provision shall be deemed stricken from this Agreement and the balance
          of this Agreement shall remain in effect unless the provision stricken is of the essence
          of this Agreement, and if any provision is inapplicable to any circumstance, it shall
          nevertheless remain applicable to all other circumstances.
        </p>
        <p>9.11. This Agreement is effective upon electronic acceptance by the Creator.</p>
      </section>
    </article>
  );
}
